import type { DeckIR, SlideIR, SlideMaster, Theme } from '../ir/deck.js';
import type { Rgba } from '../ir/color.js';
import { rgbaToCss } from '../ir/color.js';
import { angleToDegrees, emuToPixels } from '../ir/units.js';
import type { AbsoluteBounds, ShapeGeometry } from '../ir/geometry.js';
import type { ColorContext } from '../ir/resolve-color.js';
import { DEFAULT_COLOR_MAP, resolveColor } from '../ir/resolve-color.js';
import type { Shape, TableCell } from '../ir/shape.js';
import type { Effect, Fill, Line } from '../ir/style.js';
import type { Hyperlink, Paragraph, TextBody } from '../ir/text.js';
import { presetToPath } from './presets.js';
import { compileTimeline } from './timeline.js';
import type {
  Paint,
  Placement,
  RenderItem,
  RenderParagraph,
  RenderShadow,
  RenderTableCell,
  RenderTextBlock,
  RenderTextRun,
  RenderedSlide,
  Stroke,
} from './model.js';

export interface CompileContext {
  readonly deck: DeckIR;
  /** Maps a media asset id to the href the player will load. */
  readonly mediaHref: (mediaId: string) => string;
  /** Maps a slide id to a player route, for slide-jump hyperlinks. */
  readonly slideHref?: (slideId: string) => string;
  readonly fontFallback?: (font: string) => string;
}

export interface CompileResult {
  readonly slide: RenderedSlide;
  /** OOXML constructs that could not be represented. Feeds the fidelity report. */
  readonly dropped: readonly string[];
}

const DEFAULT_FONT_STACK = 'system-ui, "Segoe UI", sans-serif';

export function compileSlide(slide: SlideIR, ctx: CompileContext): CompileResult {
  const dropped = new Set<string>();
  const layout = ctx.deck.layouts[slide.layoutIndex];
  const master = layout ? ctx.deck.masters[layout.masterIndex] : undefined;
  const theme = pickTheme(ctx.deck, master);
  const colorCtx: ColorContext = {
    scheme: theme.colorScheme,
    colorMap: layout?.colorMap ?? master?.colorMap ?? DEFAULT_COLOR_MAP,
  };

  const env: Env = { ctx, theme, colorCtx, dropped, deck: ctx.deck };
  const items: RenderItem[] = [];

  // Slideshow order: master shapes, then layout shapes, then the slide's own.
  // Placeholder shapes on the master and layout are authoring prompts and are
  // never drawn during playback, so they are skipped rather than rendered empty.
  if (slide.showMasterShapes && master) {
    pushShapes(items, master.shapes.filter(isNotPlaceholder), env);
  }
  if (slide.showMasterShapes && layout && layout.showMasterShapes !== false) {
    pushShapes(items, layout.shapes.filter(isNotPlaceholder), env);
  }
  pushShapes(items, slide.shapes, env);

  const background =
    slide.background ?? layout?.background ?? master?.background ?? ({ kind: 'inherit' } as Fill);

  const width = round(emuToPixels(ctx.deck.slideWidth));
  const height = round(emuToPixels(ctx.deck.slideHeight));

  // The timeline is compiled here, from the same items the player will draw, so
  // an animation can never address a shape that was dropped during layout.
  const placements = new Map(items.map((item) => [item.id, item.placement]));
  const timeline = compileTimeline(slide.timing, {
    stageWidth: width,
    stageHeight: height,
    boundsOf: (shapeId) => placements.get(shapeId),
  });
  for (const construct of timeline?.unsupported ?? []) dropped.add(construct);
  const playable = timeline !== undefined && (timeline.groups.length > 0 || timeline.triggers.length > 0);

  return {
    slide: {
      width,
      height,
      background: toPaint(background, env, { type: 'solid', color: '#FFFFFF' }),
      items,
      ...(playable && timeline ? { timeline } : {}),
    },
    dropped: [...dropped].sort(),
  };
}

interface Env {
  readonly ctx: CompileContext;
  readonly deck: DeckIR;
  readonly theme: Theme;
  readonly colorCtx: ColorContext;
  readonly dropped: Set<string>;
}

function pickTheme(deck: DeckIR, master: SlideMaster | undefined): Theme {
  const fallback: Theme = {
    name: 'default',
    colorScheme: {
      dk1: '000000',
      lt1: 'FFFFFF',
      dk2: '44546A',
      lt2: 'E7E6E6',
      accent1: '4472C4',
      accent2: 'ED7D31',
      accent3: 'A5A5A5',
      accent4: 'FFC000',
      accent5: '5B9BD5',
      accent6: '70AD47',
      hlink: '0563C1',
      folHlink: '954F72',
    },
    fontScheme: {
      name: 'default',
      major: { latin: 'Calibri Light', eastAsian: '', complexScript: '', scripts: {} },
      minor: { latin: 'Calibri', eastAsian: '', complexScript: '', scripts: {} },
    },
    formatScheme: { fills: [], lines: [], effects: [], backgroundFills: [] },
  };
  if (!master) return deck.themes[0] ?? fallback;
  return deck.themes[master.themeIndex] ?? deck.themes[0] ?? fallback;
}

function isNotPlaceholder(shape: Shape): boolean {
  return shape.placeholder === undefined;
}

function pushShapes(out: RenderItem[], shapes: readonly Shape[], env: Env): void {
  for (const shape of shapes) {
    if (shape.hidden) continue;
    if (shape.kind === 'group') {
      // Children already carry absolute bounds, so a group needs no container.
      pushShapes(out, shape.children, env);
      continue;
    }
    const item = compileShape(shape, env);
    if (item) out.push(item);
  }
}

function compileShape(shape: Shape, env: Env): RenderItem | undefined {
  switch (shape.kind) {
    case 'shape':
    case 'connector': {
      const geometry = shape.kind === 'shape' ? shape.geometry : shape.geometry;
      const { form, path, cornerRadius } = resolveGeometry(geometry, shape.bounds, env);
      const fill = shape.kind === 'shape' ? toPaint(shape.fill, env, { type: 'none' }) : { type: 'none' as const };
      const effects = shape.kind === 'shape' ? shape.effects : [];
      const item: RenderItem = {
        kind: 'shape',
        id: shape.id,
        name: shape.name,
        placement: toPlacement(shape.bounds),
        shape: form,
        ...(path ? { path } : {}),
        ...(cornerRadius !== undefined ? { cornerRadius } : {}),
        fill,
        ...(shape.line ? { stroke: toStroke(shape.line, env) } : {}),
        ...(shadowOf(effects, env) ? { shadow: shadowOf(effects, env) as RenderShadow } : {}),
        ...(shape.kind === 'shape' && shape.text
          ? { text: compileTextBody(shape.text, env) }
          : {}),
        ...linkFields(shape.hyperlink, env),
        ...(shape.altText ? { altText: shape.altText } : {}),
      };
      return item;
    }

    case 'picture': {
      if (shape.linkedTarget) {
        env.dropped.add('picture:linked-not-embedded');
      }
      return {
        kind: 'image',
        id: shape.id,
        name: shape.name,
        placement: toPlacement(shape.bounds),
        href: shape.mediaId ? env.ctx.mediaHref(shape.mediaId) : '',
        altText: shape.altText ?? shape.name,
        ...(shape.line ? { stroke: toStroke(shape.line, env) } : {}),
        ...(shadowOf(shape.effects, env) ? { shadow: shadowOf(shape.effects, env) as RenderShadow } : {}),
        ...(shape.cropLeft || shape.cropTop || shape.cropRight || shape.cropBottom
          ? {
              crop: {
                l: shape.cropLeft,
                t: shape.cropTop,
                r: shape.cropRight,
                b: shape.cropBottom,
              },
            }
          : {}),
        ...(shape.hyperlink?.kind === 'external' ? { hrefLink: shape.hyperlink.url } : {}),
      };
    }

    case 'table':
      return {
        kind: 'table',
        id: shape.id,
        name: shape.name,
        placement: toPlacement(shape.bounds),
        columnWidths: shape.columnWidths.map((w) => round(emuToPixels(w))),
        rows: shape.rows.map((row) => ({
          height: round(emuToPixels(row.height)),
          cells: row.cells.map((cell) => compileCell(cell, env)),
        })),
      };

    case 'media':
      return {
        kind: 'media',
        id: shape.id,
        name: shape.name,
        placement: toPlacement(shape.bounds),
        mediaKind: shape.mediaKind,
        ...(shape.mediaId ? { href: env.ctx.mediaHref(shape.mediaId) } : {}),
        ...(shape.posterMediaId ? { posterHref: env.ctx.mediaHref(shape.posterMediaId) } : {}),
        autoPlay: shape.autoPlay,
        loop: shape.loop,
        ...(shape.volume !== undefined && shape.volume < 1 ? { volume: shape.volume } : {}),
        ...(shape.muted ? { muted: true } : {}),
        altText: shape.altText ?? shape.name,
      };

    case 'graphicFrame': {
      env.dropped.add(`graphicFrame:${shape.frameKind}`);
      if (shape.fallbackMediaId) {
        return {
          kind: 'image',
          id: shape.id,
          name: shape.name,
          placement: toPlacement(shape.bounds),
          href: env.ctx.mediaHref(shape.fallbackMediaId),
          altText: shape.altText ?? `${shape.frameKind} (rendered as an image)`,
        };
      }
      // No fallback part: draw nothing rather than an empty box the author did
      // not author. The fidelity report is where this becomes visible.
      return undefined;
    }

    case 'group':
      return undefined;
  }
}

function compileCell(cell: TableCell, env: Env): RenderTableCell {
  return {
    colSpan: cell.gridSpan,
    rowSpan: cell.rowSpan,
    merged: cell.hMerge || cell.vMerge,
    fill: toPaint(cell.fill, env, { type: 'none' }),
    text: compileTextBody(cell.text, env, cell.anchor === 'ctr' ? 'middle' : cell.anchor === 'b' ? 'bottom' : 'top'),
    borders: {
      ...(cell.borderLeft ? { left: toStroke(cell.borderLeft, env) } : {}),
      ...(cell.borderRight ? { right: toStroke(cell.borderRight, env) } : {}),
      ...(cell.borderTop ? { top: toStroke(cell.borderTop, env) } : {}),
      ...(cell.borderBottom ? { bottom: toStroke(cell.borderBottom, env) } : {}),
    },
  };
}

function resolveGeometry(
  geometry: ShapeGeometry | undefined,
  bounds: AbsoluteBounds,
  env: Env,
): { form: 'rect' | 'roundRect' | 'ellipse' | 'path'; path?: string; cornerRadius?: number } {
  if (!geometry || geometry.kind === 'none') return { form: 'rect' };
  if (geometry.kind === 'custom') {
    const path = customPathToSvg(geometry, bounds);
    return path ? { form: 'path', path } : { form: 'rect' };
  }
  const preset = presetToPath(geometry.preset, {
    w: emuToPixels(bounds.cx),
    h: emuToPixels(bounds.cy),
    adjustments: geometry.adjustments,
  });
  if (preset.kind === 'unsupported') {
    env.dropped.add(`geometry:${geometry.preset}`);
    return { form: 'rect' };
  }
  if (preset.kind === 'rect') return { form: 'rect' };
  if (preset.kind === 'ellipse') return { form: 'ellipse' };
  if (preset.kind === 'roundRect') return { form: 'roundRect', cornerRadius: preset.radius };
  return { form: 'path', path: preset.path };
}

function customPathToSvg(
  geometry: { kind: 'custom'; paths: readonly { w: number; h: number; commands: readonly unknown[] }[] },
  bounds: AbsoluteBounds,
): string | undefined {
  const parts: string[] = [];
  for (const p of geometry.paths) {
    const sx = p.w === 0 ? 1 : emuToPixels(bounds.cx) / emuToPixels(p.w);
    const sy = p.h === 0 ? 1 : emuToPixels(bounds.cy) / emuToPixels(p.h);
    for (const raw of p.commands) {
      const cmd = raw as { op: string; pts?: { x: number; y: number }[] };
      const pts = (cmd.pts ?? []).map(
        (pt) => `${round(emuToPixels(pt.x) * sx)},${round(emuToPixels(pt.y) * sy)}`,
      );
      switch (cmd.op) {
        case 'moveTo':
          if (pts[0]) parts.push(`M ${pts[0]}`);
          break;
        case 'lnTo':
          if (pts[0]) parts.push(`L ${pts[0]}`);
          break;
        case 'cubicBezTo':
          if (pts.length >= 3) parts.push(`C ${pts[0]} ${pts[1]} ${pts[2]}`);
          break;
        case 'quadBezTo':
          if (pts.length >= 2) parts.push(`Q ${pts[0]} ${pts[1]}`);
          break;
        case 'close':
          parts.push('Z');
          break;
        default:
          break;
      }
    }
  }
  return parts.length > 0 ? parts.join(' ') : undefined;
}

export function toPlacement(b: AbsoluteBounds): Placement {
  return {
    x: round(emuToPixels(b.x)),
    y: round(emuToPixels(b.y)),
    w: round(emuToPixels(b.cx)),
    h: round(emuToPixels(b.cy)),
    rot: round(angleToDegrees(b.rot)),
    flipH: b.flipH,
    flipV: b.flipV,
  };
}

export function toPaint(fill: Fill, env: Env, fallback: Paint = { type: 'none' }): Paint {
  switch (fill.kind) {
    case 'none':
      return { type: 'none' };
    case 'inherit':
      return fallback;
    case 'solid':
      return { type: 'solid', color: css(resolveColor(fill.color, env.colorCtx)) };
    case 'gradient': {
      const stops = fill.stops.map((s) => ({
        offset: s.position,
        color: css(resolveColor(s.color, env.colorCtx)),
      }));
      if (fill.path && fill.path !== 'linear') {
        return { type: 'radialGradient', stops };
      }
      return { type: 'linearGradient', angle: fill.angle ?? 90, stops };
    }
    case 'blip':
      return {
        type: 'image',
        href: env.ctx.mediaHref(fill.mediaId),
        mode: fill.mode,
        opacity: fill.alpha ?? 1,
      };
    case 'pattern':
      // Pattern fills degrade to their foreground colour rather than vanishing.
      env.dropped.add(`fill:pattern:${fill.preset}`);
      return { type: 'solid', color: css(resolveColor(fill.foreground, env.colorCtx)) };
    case 'group':
      return fallback;
  }
}

function toStroke(line: Line, env: Env): Stroke {
  const paint = toPaint(line.fill, env, { type: 'solid', color: '#000000' });
  const color = paint.type === 'solid' ? paint.color : '#000000';
  const width = line.width === 0 ? 1 : round(emuToPixels(line.width));
  return {
    color,
    width,
    ...(dashArray(line.dash, width) ? { dashArray: dashArray(line.dash, width) as number[] } : {}),
    cap: line.cap === 'rnd' ? 'round' : line.cap === 'sq' ? 'square' : 'butt',
    join: line.join === 'round' ? 'round' : line.join === 'bevel' ? 'bevel' : 'miter',
  };
}

function dashArray(dash: Line['dash'], width: number): number[] | undefined {
  const w = Math.max(width, 1);
  switch (dash) {
    case 'solid':
      return undefined;
    case 'dot':
    case 'sysDot':
      return [w, w * 2];
    case 'dash':
    case 'sysDash':
      return [w * 4, w * 3];
    case 'lgDash':
      return [w * 8, w * 3];
    case 'dashDot':
    case 'sysDashDot':
      return [w * 4, w * 3, w, w * 3];
    case 'lgDashDot':
      return [w * 8, w * 3, w, w * 3];
    case 'lgDashDotDot':
    case 'sysDashDotDot':
      return [w * 8, w * 3, w, w * 3, w, w * 3];
  }
}

function shadowOf(effects: readonly Effect[], env: Env): RenderShadow | undefined {
  const shadow = effects.find((e) => e.kind === 'outerShadow');
  if (!shadow || shadow.kind !== 'outerShadow') return undefined;
  const rad = (shadow.direction * Math.PI) / 180;
  const distance = emuToPixels(shadow.distance);
  return {
    color: css(resolveColor(shadow.color, env.colorCtx)),
    blur: round(emuToPixels(shadow.blurRadius)),
    offsetX: round(Math.cos(rad) * distance),
    offsetY: round(Math.sin(rad) * distance),
  };
}

function linkFields(
  link: Hyperlink | undefined,
  env: Env,
): { href?: string; hrefKind?: 'external' | 'slide' | 'action' } {
  if (!link) return {};
  switch (link.kind) {
    case 'external':
      return { href: link.url, hrefKind: 'external' };
    case 'slide':
      return {
        href: env.ctx.slideHref ? env.ctx.slideHref(link.slideId) : `#slide-${link.slideId}`,
        hrefKind: 'slide',
      };
    case 'action':
      return { href: link.verb, hrefKind: 'action' };
  }
}

export function compileTextBody(
  body: TextBody,
  env: Env,
  anchorOverride?: 'top' | 'middle' | 'bottom',
): RenderTextBlock {
  const anchor =
    anchorOverride ??
    (body.bodyProps.anchor === 'ctr' ? 'middle' : body.bodyProps.anchor === 'b' ? 'bottom' : 'top');
  const scale =
    body.bodyProps.autofit.kind === 'normal' ? body.bodyProps.autofit.fontScale : 1;
  return {
    anchor,
    insets: {
      left: round(emuToPixels(body.bodyProps.insetLeft)),
      top: round(emuToPixels(body.bodyProps.insetTop)),
      right: round(emuToPixels(body.bodyProps.insetRight)),
      bottom: round(emuToPixels(body.bodyProps.insetBottom)),
    },
    wrap: body.bodyProps.wrap !== 'none',
    vertical: body.bodyProps.vertical !== 'horz',
    paragraphs: body.paragraphs.map((p) => compileParagraph(p, env, scale)),
  };
}

function compileParagraph(p: Paragraph, env: Env, scale: number): RenderParagraph {
  const spacing = p.props.lineSpacing;
  return {
    align: alignOf(p.props.align, p.props.rtl === true),
    level: p.props.level,
    marginLeft: round(emuToPixels(p.props.marginLeft ?? 0)),
    indent: round(emuToPixels(p.props.indent ?? 0)),
    spaceBefore: spacingToPx(p.props.spaceBefore),
    spaceAfter: spacingToPx(p.props.spaceAfter),
    lineHeight:
      spacing?.kind === 'points' ? round(spacing.value * (96 / 72)) : (spacing?.value ?? 100) / 100,
    lineHeightKind: spacing?.kind === 'points' ? 'points' : 'percent',
    ...(bulletOf(p, env) ? { bullet: bulletOf(p, env) as { text: string } } : {}),
    rtl: p.props.rtl === true,
    runs: p.runs
      .filter((r) => r.kind !== 'break')
      .map((r) => compileRun(r as Extract<typeof r, { props: unknown; text?: string }>, env, scale)),
  };
}

function compileRun(
  run: { text?: string; props: Paragraph['runs'][number]['props'] },
  env: Env,
  scale: number,
): RenderTextRun {
  const props = run.props;
  const sizePt = (props.size ?? 1800) / 100;
  const family = props.latinFont ?? env.theme.fontScheme.minor.latin;
  const fill = props.fill;
  const color =
    fill && fill.kind === 'solid' ? css(resolveColor(fill.color, env.colorCtx)) : '#000000';
  const link = props.hyperlink;
  return {
    text: run.text ?? '',
    fontFamily: fontStack(family, env),
    fontSize: round(sizePt * scale * (96 / 72)),
    bold: props.bold === true,
    italic: props.italic === true,
    underline: props.underline !== undefined && props.underline !== 'none',
    strike: props.strike !== undefined && props.strike !== 'noStrike',
    color,
    ...(props.highlight ? { highlight: css(resolveColor(props.highlight, env.colorCtx)) } : {}),
    ...(props.spacing ? { letterSpacing: round((props.spacing / 100) * (96 / 72)) } : {}),
    ...(props.baseline ? { baselineShift: props.baseline / 1000 } : {}),
    ...(link ? linkFields(link, env) : {}),
    ...(props.language ? { lang: props.language } : {}),
  };
}

function bulletOf(p: Paragraph, env: Env): { text: string; font?: string; color?: string } | undefined {
  const b = p.props.bullet;
  if (!b || b.kind === 'none') return undefined;
  if (b.kind === 'char') {
    return {
      text: b.char,
      ...(b.font ? { font: b.font } : {}),
      ...(b.color ? { color: css(resolveColor(b.color, env.colorCtx)) } : {}),
    };
  }
  if (b.kind === 'autoNum') {
    // Numbering is resolved by the player, which knows the sibling order.
    return { text: '', ...(b.font ? { font: b.font } : {}) };
  }
  env.dropped.add('bullet:picture');
  return { text: '•' };
}

function alignOf(align: Paragraph['props']['align'], rtl: boolean): RenderParagraph['align'] {
  switch (align) {
    case 'ctr':
      return 'center';
    case 'r':
      return 'right';
    case 'just':
    case 'justLow':
    case 'dist':
    case 'thaiDist':
      return 'justify';
    case 'l':
      return 'left';
    default:
      return rtl ? 'right' : 'left';
  }
}

function spacingToPx(s: Paragraph['props']['spaceBefore']): number {
  if (!s) return 0;
  return s.kind === 'points' ? round(s.value * (96 / 72)) : 0;
}

function fontStack(font: string | undefined, env: Env): string {
  const resolved = env.ctx.fontFallback ? env.ctx.fontFallback(font ?? '') : font;
  return resolved && resolved !== '' ? `"${resolved}", ${DEFAULT_FONT_STACK}` : DEFAULT_FONT_STACK;
}

function css(c: Rgba): string {
  return rgbaToCss(c);
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
